import { useUserOrders } from '@quizzach/api-hooks';
import { AlertMessage } from '@quizzach/components';
import { useWindowListener } from '@quizzach/contexts';
import { ApiFailedReason } from '@quizzach/interfaces';
import {
	convertUnixTimestamp,
	formatCartPrice,
	handleQuizType,
} from '@quizzach/utils';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
	BtnLine,
	Sk,
	TxEmpty,
	TxGrid,
	TxMore,
	TxTicket,
} from '@quizzach/styles';

const MOBILE_BREAK = 720;
const PAGE_SIZE_DESKTOP = 8;
const PAGE_SIZE_MOBILE = 5;

export const UserStatsHistory = () => {
	const { t } = useTranslation();
	const { orders, error, loading, getOrders } = useUserOrders();
	const { width } = useWindowListener();
	const [pagesShown, setPagesShown] = useState(1);
	// tickets from this index on came from »učitaj još« and fade in staggered
	const [freshFrom, setFreshFrom] = useState<number | null>(null);

	const pageSize = width <= MOBILE_BREAK ? PAGE_SIZE_MOBILE : PAGE_SIZE_DESKTOP;
	const sortedOrders = [...orders].sort((a, b) => +b.paid_at - +a.paid_at);
	const visibleOrders = sortedOrders.slice(0, pagesShown * pageSize);
	const remaining = sortedOrders.length - visibleOrders.length;

	useEffect(() => {
		getOrders();
	}, []);

	if (error) {
		return (
			<div style={{ padding: '30px var(--pad)' }}>
				<AlertMessage
					code={ApiFailedReason.default}
					className='alert'
					type='error'
					style={{ margin: '0px auto' }}
				/>
			</div>
		);
	}

	if (loading) {
		return (
			<div aria-hidden='true'>
				<TxGrid>
					{Array.from({ length: pageSize }).map((_, index) => (
						<TxTicket key={index}>
							{/* min-heights match typical real tickets (1–2 item lines,
							    titles usually wrap once at grid column widths) so the
							    content below barely settles when the data lands */}
							<div
								className='tkt'
								style={{
									display: 'flex',
									flexDirection: 'column',
									minHeight: index % 2 === 0 ? 265 : 232,
								}}
							>
								<header className='hd'>
									<Sk style={{ height: 24, width: 130, margin: '0 auto' }} />
									<Sk style={{ height: 12, width: 88, margin: '6px auto 0' }} />
								</header>
								<Sk style={{ height: 15, marginTop: 20 }} />
								{index % 2 === 0 && (
									<Sk style={{ height: 15, width: '72%', marginTop: 14 }} />
								)}
								<div className='tot' style={{ marginTop: 'auto' }}>
									<Sk style={{ height: 13, width: 64 }} />
									<Sk style={{ height: 26, width: 96 }} />
								</div>
							</div>
							<div className='zig' />
						</TxTicket>
					))}
				</TxGrid>
				{/* reserve the load-more strip so the gratis panel below doesn't
				    shift when the button appears with the data */}
				<TxMore>
					<Sk
						style={{
							height: 55,
							width: 158,
							margin: '0 auto',
							borderRadius: 999,
						}}
					/>
				</TxMore>
			</div>
		);
	}

	if (orders.length === 0) {
		return <TxEmpty>{t('profile.transactions.empty')}</TxEmpty>;
	}

	return (
		<>
			<TxGrid data-id='user-transactions'>
				{visibleOrders.map((order, index) => (
					<TxTicket
						key={order.uuid}
						{...(freshFrom !== null && index >= freshFrom
							? {
									className: 'fade-new',
									style: { animationDelay: `${(index - freshFrom) * 45}ms` },
								}
							: {})}
					>
						<div className='tkt'>
							<header className='hd'>
								<span className='date'>
									{order.paid_at && convertUnixTimestamp(+order.paid_at)}
								</span>
								<span className='no'>
									{t('profile.transactions.receipt', {
										id: order.order_number,
									})}
								</span>
							</header>
							{order.cart_items.map((item) => {
								const quiz = item?.created_quiz;
								const author = quiz?.premade_quiz?.author?.display_name;

								return (
									<div className='li' key={quiz?.uuid ?? item?.price}>
										<span className='nm'>
											{quiz?.quiz_type === 'premade' ? (
												<>
													{quiz?.premade_quiz?.title}
													{author && author !== 'quizzach' && (
														<span className='by'>
															{' '}
															{t('cart.quizType.byAuthor', { author })}
														</span>
													)}
												</>
											) : (
												handleQuizType(
													quiz?.quiz_type,
													quiz?.number_of_questions - 1,
													undefined,
													item?.reserved_questions_count
												)
											)}
										</span>
										<span className='pr'>{formatCartPrice(item?.price)}</span>
									</div>
								);
							})}
							<div className='tot'>
								<span className='lb'>{t('cart.summary.total')}</span>
								<span className='v'>
									{formatCartPrice(order.total_price)} EUR
								</span>
							</div>
						</div>
						<div className='zig' />
					</TxTicket>
				))}
			</TxGrid>
			{remaining > 0 && (
				<TxMore>
					<BtnLine
						type='button'
						onClick={() => {
							setFreshFrom(visibleOrders.length);
							setPagesShown((pages) => pages + 1);
						}}
					>
						{t('premade.redesign.loadMore')}
					</BtnLine>
				</TxMore>
			)}
		</>
	);
};
